import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Box,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Lock as LockIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { FDI_NOTATION, validateDentalChartData } from "./DentalChartUtils";
import ToothSVG from "./ToothSVG";
import DentalChartLegend from "./DentalChartLegend";
import useDentalPerformance from "./useDentalPerformance";
import useDentalAccessibility from "./useDentalAccessibility";
import useDentalResponsive from "./useDentalResponsive";
import Axios from "axios";
import useUserState from "../../hooks/useUserState";
import useBDocTranslator from "../../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  dentalChart: {
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    minHeight: "250px",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1),
      minHeight: "300px",
    },
  },
  quadrant: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    margin: theme.spacing(0.25),
    minHeight: "100px",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(0.75),
      margin: theme.spacing(0.5),
      minHeight: "110px",
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(1),
      minHeight: "120px",
    },
  },
  quadrantTitle: {
    fontSize: "0.7rem",
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.8rem",
      marginBottom: theme.spacing(0.5),
    },
  },
  toothContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(8, 1fr)",
    gap: theme.spacing(0.25),
    justifyItems: "center",
    alignItems: "center",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    padding: theme.spacing(0.25),
    [theme.breakpoints.up("sm")]: {
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.5),
    },
    [theme.breakpoints.up("md")]: {
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.75),
    },
  },
  chartInfo: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.75),
    backgroundColor: theme.palette.primary.main + "12",
    borderRadius: theme.spacing(0.5),
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    wordBreak: "break-word",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
  },
  loadingContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  errorContainer: {
    textAlign: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light + "22",
    border: `1px solid ${theme.palette.error.light}`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(0.75),
    flexDirection: "row",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    justifyContent: "flex-start",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(0.25),
      marginBottom: theme.spacing(0.5),
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  performanceInfo: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    fontSize: "0.75rem",
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    wordBreak: "break-word",
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      fontSize: "0.875rem",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.25),
      fontSize: "0.7rem",
    },
  },
  responsiveQuadrantContainer: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    "& .MuiGrid-container": {
      margin: 0,
      width: "100%",
      maxWidth: "100%",
      justifyContent: "center",
      gap: theme.spacing(0.5),
      boxSizing: "border-box",
      [theme.breakpoints.up("md")]: {
        gap: theme.spacing(1),
      },
    },
    "& .MuiGrid-item": {
      padding: theme.spacing(0.25),
      flex: "1 1 auto",
      width: "100%",
      minWidth: "0",
      maxWidth: "100%",
      boxSizing: "border-box",
      overflow: "hidden",
      [theme.breakpoints.up("sm")]: {
        padding: theme.spacing(0.5),
      },
    },
  },
  mobileOptimized: {
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      maxWidth: "100%",
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      "& .MuiChip-root": {
        height: "22px",
        fontSize: "0.65rem",
        maxWidth: "none",
        flex: "0 1 auto",
        "& .MuiChip-label": {
          paddingLeft: "4px",
          paddingRight: "4px",
          whiteSpace: "nowrap",
        },
      },
      "& .MuiButton-root": {
        minWidth: "auto",
        padding: theme.spacing(0.25),
        fontSize: "0.7rem",
        flex: "0 0 auto",
      },
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      "& .MuiChip-root": {
        height: "20px",
        fontSize: "0.6rem",
        width: "100%",
        maxWidth: "100%",
      },
      "& .MuiButton-root": {
        padding: theme.spacing(0.25),
        fontSize: "0.65rem",
        width: "100%",
      },
    },
  },
  // New styles for blocked state and inline initialization
  blockedContainer: {
    position: "relative",
    filter: "blur(2px)",
    pointerEvents: "none",
    opacity: 0.5,
    transition: "all 0.3s ease-in-out",
  },
  blockedOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    borderRadius: theme.spacing(1),
  },
  initializationForm: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[3],
    maxWidth: "400px",
    width: "100%",
    margin: "0 auto",
  },
  initializationContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    border: `2px dashed ${theme.palette.divider}`,
  },
  formActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    justifyContent: "flex-end",
  },
  // Access denied styles
  accessDeniedContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.warning.light + "22",
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.warning.main}`,
    margin: theme.spacing(2),
  },
  accessDeniedIcon: {
    fontSize: "64px",
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  accessDeniedTitle: {
    color: theme.palette.warning.dark,
    marginBottom: theme.spacing(1),
  },
  accessDeniedMessage: {
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  contactAdminButton: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const DentalChartComponent = ({
  patientId,
  providerId,
  onRefresh,
  initializeMode,
  onAccessDenied,
  onDentalDataExists,
}) => {
  const classes = useStyles();
  const [dentalData, setDentalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartInfo, setChartInfo] = useState(null);
  const [saving, setSaving] = useState(false);

  // New states for initialization
  const [isInitializing, setIsInitializing] = useState(false);
  const [chartType, setChartType] = useState("adult");
  const [notes, setNotes] = useState("");
  const [initializationError, setInitializationError] = useState("");

  // Access control states
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessError, setAccessError] = useState(null);

  const userLocalState = useUserState();
  const { t } = useBDocTranslator();

  // Performance hooks
  const { optimizeDentalData, performanceUtils, debounce, setCache } =
    useDentalPerformance();

  // Accessibility hooks
  const {
    getDentalChartAriaProps,
    getQuadrantAriaProps,
    announceDentalStats,
    setupKeyboardShortcuts,
  } = useDentalAccessibility();

  // Responsive hooks
  const {
    layoutConfig,
    getQuadrantConfig,
    getToothConfig,
    getStatsConfig,
    getGridProps,
    getDimensions,
    getTouchHandlers,
    shouldRender,
  } = useDentalResponsive();

  // Memoización de estadísticas dentales
  const dentalStats = useMemo(() => {
    if (Object.keys(dentalData).length === 0) return null;
    performanceUtils.startOperation("dental_stats");
    const stats = optimizeDentalData.calculateDentalStats(dentalData);
    performanceUtils.endOperation("dental_stats");
    return stats;
  }, [dentalData, optimizeDentalData, performanceUtils]);

  // Removed unused groupedTeeth memoization

  // Handle initialization mode from parent component
  useEffect(() => {
    if (initializeMode && !chartInfo) {
      setIsInitializing(true);
    }
  }, [initializeMode, chartInfo]);

  // Cargar datos del odontograma - solo cuando cambie patientId
  useEffect(() => {
    if (!patientId) return;

    const fetchData = async () => {
      if (!userLocalState[0]?.jwt) return;

      try {
        setLoading(true);
        setError(null);

        // Obtener providerId del usuario logueado si no se proporciona
        const effectiveProviderId =
          providerId || userLocalState[0]?.extendedData?.id;

        // Construir URL con provider_id como query parameter
        const url = new URL(
          `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/chart/patient/${patientId}`
        );
        if (effectiveProviderId) {
          url.searchParams.append("provider_id", effectiveProviderId);
        }

        const response = await Axios.get(url.toString(), {
          headers: { Authorization: "Bearer " + userLocalState[0].jwt },
        });

        if (response.data) {
          // El API devuelve { message, data: [array], count }
          // Necesitamos tomar el primer odontograma del array
          const dentalCharts = response.data.data || [];
          if (dentalCharts.length > 0) {
            const firstChart = dentalCharts[0];
            setChartInfo(firstChart);
            setDentalData(firstChart.teeth_data || {});
            // Notificar que hay datos existentes
            onDentalDataExists?.(true);
          } else {
            // No hay odontogramas para este paciente
            setChartInfo(null);
            setDentalData({});
            // Notificar que no hay datos
            onDentalDataExists?.(false);
          }
        }
      } catch (error) {
        console.log("Error fetching dental chart:", error);
        // Manejar diferentes códigos de error
        if (error.response?.status === 404) {
          // No hay odontograma aún
          setChartInfo(null);
          setDentalData({});
          // Notificar que no hay datos
          onDentalDataExists?.(false);
        } else if (error.response?.status === 403) {
          // Acceso denegado a funcionalidades dentales
          const errorData = error.response.data;
          setAccessDenied(true);
          setAccessError({
            message:
              errorData.message || t("Access denied to dental functionality"),
            reason:
              errorData.reason ||
              t("Provider does not have dental EHR enabled"),
            error_code: errorData.error_code || "DENTAL_ACCESS_DENIED",
          });
          onAccessDenied?.(true);
        } else {
          setError(t("Error loading dental chart"));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]); // Solo depende de patientId para evitar bucles infinitos

  // Removed unused fetchDentalChart function

  // Configurar shortcuts de teclado para accesibilidad
  useEffect(() => {
    const cleanup = setupKeyboardShortcuts();
    return cleanup;
  }, [setupKeyboardShortcuts]);

  // Anunciar estadísticas cuando cambien
  useEffect(() => {
    if (dentalStats) {
      announceDentalStats(dentalStats);
    }
  }, [dentalStats, announceDentalStats]);

  // Función de actualización optimizada con debouncing
  const updateToothStatus = useCallback(
    async (toothNumber, newStatus) => {
      try {
        performanceUtils.startOperation("tooth_update");
        setSaving(true);
        setError(null);

        // Verificar que tenemos los datos necesarios
        if (!patientId) {
          setError(t("Patient ID is required"));
          return;
        }

        // Obtener providerId del usuario logueado si no se proporciona
        const effectiveProviderId =
          providerId || userLocalState[0]?.extendedData?.id;

        // Debug logging para identificar el problema
        console.log("Debug Dental Chart:", {
          providerId,
          userLocalState: userLocalState[0],
          effectiveProviderId,
          patientId,
        });

        if (!effectiveProviderId) {
          setError(t("Provider ID is required"));
          return;
        }

        const updatedData = {
          ...dentalData,
          [toothNumber]: newStatus,
        };

        const chartPayload = {
          teeth_data: updatedData,
          last_examination_date: new Date().toISOString(),
          notes: chartInfo?.notes || "",
        };

        // Validar datos antes de enviar
        const validation = validateDentalChartData({
          patient_id: patientId,
          provider_id: effectiveProviderId,
          chart_type: chartInfo?.chart_type || "adult",
          ...chartPayload,
        });

        if (!validation.isValid) {
          setError(validation.errors.join(", "));
          return;
        }

        if (chartInfo?.id) {
          // Actualizar odontograma existente
          await Axios.put(
            `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/chart/${chartInfo.id}`,
            chartPayload,
            {
              headers: { Authorization: "Bearer " + userLocalState[0]?.jwt },
            }
          );
        } else {
          // Crear nuevo odontograma
          const newChartResponse = await Axios.post(
            `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/chart`,
            {
              patient_id: patientId,
              provider_id: effectiveProviderId,
              chart_type: "adult",
              ...chartPayload,
            },
            {
              headers: { Authorization: "Bearer " + userLocalState[0]?.jwt },
            }
          );

          setChartInfo(newChartResponse.data);
        }

        setDentalData(updatedData);
        setCache(`dental_chart_${patientId}`, updatedData, 300000); // Cache por 5 minutos
        onRefresh?.();
        performanceUtils.endOperation("tooth_update");
      } catch (error) {
        console.error("Error updating dental chart:", error);
        if (error.response?.status === 403) {
          // Acceso denegado durante la actualización
          const errorData = error.response.data;
          setAccessDenied(true);
          setAccessError({
            message:
              errorData.message || t("Access denied to dental functionality"),
            reason:
              errorData.reason ||
              t("Provider does not have dental EHR enabled"),
            error_code: errorData.error_code || "DENTAL_ACCESS_DENIED",
          });
          onAccessDenied?.(true);
        } else {
          setError(t("Error saving dental chart"));
        }
      } finally {
        setSaving(false);
      }
    },
    [
      dentalData,
      chartInfo,
      patientId,
      providerId,
      userLocalState,
      t,
      setCache,
      performanceUtils,
      onRefresh,
      onAccessDenied,
    ]
  );

  // Create new dental chart inline
  const handleCreateChart = useCallback(async () => {
    if (!patientId) {
      setInitializationError(t("Patient ID is required"));
      return;
    }

    setLoading(true);
    setInitializationError("");

    try {
      const effectiveProviderId =
        providerId || userLocalState[0]?.extendedData?.id;

      if (!effectiveProviderId) {
        setInitializationError(t("Provider ID is required"));
        return;
      }

      const response = await Axios.post(
        `${process.env.REACT_APP_API_URL_BASE}/dental-ehr/chart`,
        {
          patient_id: patientId,
          provider_id: effectiveProviderId,
          chart_type: chartType,
          teeth_data: {},
          notes: notes.trim(),
        },
        {
          headers: { Authorization: "Bearer " + userLocalState[0]?.jwt },
        }
      );

      setChartInfo(response.data);
      setDentalData({});
      setIsInitializing(false);
      setNotes("");
      setChartType("adult");
      onRefresh?.();
    } catch (error) {
      console.error("Error creating dental chart:", error);
      if (error.response?.status === 403) {
        // Acceso denegado durante la creación
        const errorData = error.response.data;
        setAccessDenied(true);
        setAccessError({
          message:
            errorData.message || t("Access denied to dental functionality"),
          reason:
            errorData.reason || t("Provider does not have dental EHR enabled"),
          error_code: errorData.error_code || "DENTAL_ACCESS_DENIED",
        });
        setIsInitializing(false);
        onAccessDenied?.(true);
      } else {
        setInitializationError(t("Error creating dental chart"));
      }
    } finally {
      setLoading(false);
    }
  }, [
    patientId,
    providerId,
    chartType,
    notes,
    userLocalState,
    t,
    onRefresh,
    onAccessDenied,
  ]);

  // Handler con debouncing para evitar múltiples llamadas
  const handleToothClick = useCallback(
    (toothNumber, newStatus) => {
      const debouncedFunction = debounce(updateToothStatus, 500, "tooth_click");
      return debouncedFunction(toothNumber, newStatus);
    },
    [updateToothStatus, debounce]
  );

  // Show access denied UI
  if (accessDenied && accessError) {
    return (
      <Card
        className="mb-4"
        style={{
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <CardContent>
          <div className={classes.accessDeniedContainer}>
            <LockIcon className={classes.accessDeniedIcon} />
            <Typography variant="h5" className={classes.accessDeniedTitle}>
              {t("Dental EHR Access Denied")}
            </Typography>
            <Typography variant="h6" className={classes.accessDeniedMessage}>
              {accessError.message}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ marginBottom: 16 }}
            >
              <strong>{t("Reason")}:</strong> {accessError.reason}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginBottom: 24 }}
            >
              <strong>{t("Error Code")}:</strong> {accessError.error_code}
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              style={{ marginBottom: 24 }}
            >
              {t(
                "This provider account does not have access to dental EHR functionality. Please contact your system administrator to enable dental chart features."
              )}
            </Typography>
            <Button
              variant="contained"
              className={classes.contactAdminButton}
              href="mailto:admin@admin.com"
              onClick={(e) => e.preventDefault()}
            >
              {t("Contact Administrator")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card
        style={{
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <CardContent>
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" className="mt-2">
              {t("Loading dental chart…")}
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show initialization form if chart doesn't exist
  if (!chartInfo && !loading && isInitializing) {
    return (
      <Card
        className="mb-4"
        style={{
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("Initialize Dental Chart")}
          </Typography>

          <div className={classes.initializationContainer}>
            <Typography variant="body1" gutterBottom color="textSecondary">
              {t(
                "Create a new dental chart for this patient to start tracking dental health."
              )}
            </Typography>

            <div className={classes.initializationForm}>
              {initializationError && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{ marginBottom: 16 }}
                >
                  {initializationError}
                </Typography>
              )}

              <FormControl fullWidth margin="normal">
                <InputLabel>{t("Chart Type")}</InputLabel>
                <Select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                >
                  <MenuItem value="adult">{t("Adult")}</MenuItem>
                  <MenuItem value="child">{t("Child")}</MenuItem>
                  <MenuItem value="mixed">{t("Mixed")}</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                minRows={3}
                margin="normal"
                label={t("Initial Notes (Optional)")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("Enter any initial observations or notes...")}
              />

              <div className={classes.formActions}>
                <Button
                  variant="outlined"
                  onClick={() => setIsInitializing(false)}
                  disabled={loading}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateChart}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : t("Create Chart")}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="mb-4"
      style={{
        maxWidth: "100%",
        width: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
        margin: 0,
      }}
    >
      <CardContent
        style={{ padding: "12px", width: "100%", boxSizing: "border-box" }}
      >
        <Typography variant="h6" gutterBottom style={{ marginBottom: "8px" }}>
          {t("Patient Dental Chart")}
        </Typography>

        {error && (
          <div className={classes.errorContainer}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </div>
        )}

        {chartInfo && (
          <div className={classes.chartInfo}>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontSize: "0.75rem" }}
            >
              {t("Last examination")}:{" "}
              {new Date(chartInfo.last_examination_date).toLocaleDateString()}
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontSize: "0.75rem" }}
            >
              {t("Chart type")}: {t(chartInfo.chart_type)}
            </Typography>
            {saving && (
              <Typography
                variant="body2"
                color="primary"
                style={{ fontSize: "0.75rem" }}
              >
                {t("Saving changes…")}
              </Typography>
            )}
          </div>
        )}

        {/* Estadísticas dentales */}
        {dentalStats && (
          <div
            className={`${classes.statsContainer} ${classes.mobileOptimized}`}
            data-dental-stats="true"
            role="region"
            aria-label={t("Dental Statistics")}
            style={{
              flexDirection: getStatsConfig.direction,
              gap: getDimensions.quadrantSpacing,
            }}
          >
            <Chip
              label={`${t("Total Teeth")}: ${dentalStats.totalTeeth}`}
              color="default"
              size={getStatsConfig.chipSize}
              aria-label={`${t("Total Teeth")}: ${dentalStats.totalTeeth}`}
            />
            <Chip
              label={`${t("Healthy")}: ${dentalStats.healthyCount}`}
              color="primary"
              size={getStatsConfig.chipSize}
              aria-label={`${t("Healthy teeth")}: ${dentalStats.healthyCount}`}
            />
            <Chip
              label={`${t("Problems")}: ${dentalStats.problemsCount}`}
              color={dentalStats.problemsCount > 0 ? "secondary" : "default"}
              size={getStatsConfig.chipSize}
              aria-label={`${t("Teeth with problems")}: ${
                dentalStats.problemsCount
              }`}
            />
            <Chip
              label={`${t("Health")}: ${dentalStats.healthPercentage}%`}
              color={
                dentalStats.healthPercentage >= 80 ? "primary" : "secondary"
              }
              size={getStatsConfig.chipSize}
              aria-label={`${t("Overall dental health")}: ${
                dentalStats.healthPercentage
              } percent`}
            />
          </div>
        )}

        {!chartInfo && !loading && !isInitializing && (
          <div className={classes.initializationContainer}>
            <Typography variant="h6" gutterBottom>
              {t("No Dental Chart Found")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t(
                "This patient doesn't have a dental chart yet. Click the 'New' button to create one."
              )}
            </Typography>
          </div>
        )}

        <div
          className={`${classes.dentalChart} ${classes.mobileOptimized} ${
            !chartInfo ? classes.blockedContainer : ""
          }`}
          {...getDentalChartAriaProps()}
          {...getTouchHandlers}
          data-dental-module="true"
          style={{
            width: "100%",
            maxWidth: "100%",
            padding: getDimensions.chartPadding,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {!chartInfo && (
            <div className={classes.blockedOverlay}>
              <Typography variant="h6" color="textSecondary">
                {t("Create dental chart to start")}
              </Typography>
            </div>
          )}
          {/* Instrucciones ocultas para lectores de pantalla */}
          <div
            id="dental-navigation-instructions"
            style={{
              position: "absolute",
              left: "-10000px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          >
            {t("dental_chart_aria_description")}
          </div>

          {/* Cuadrantes */}
          <div className={classes.responsiveQuadrantContainer}>
            {layoutConfig.quadrantLayout === "vertical" ? (
              // Layout vertical para móviles
              <Grid container spacing={getGridProps.spacing} direction="column">
                {/* Upper Right */}
                <Grid item xs={12}>
                  <div
                    className={classes.quadrant}
                    {...getQuadrantAriaProps(
                      t("Upper Right"),
                      FDI_NOTATION.UPPER_RIGHT.length
                    )}
                    style={getQuadrantConfig("upper-right")}
                  >
                    {shouldRender.quadrantTitles && (
                      <Typography className={classes.quadrantTitle}>
                        {t("Upper Right")} (1°)
                      </Typography>
                    )}
                    <div className={classes.toothContainer}>
                      {FDI_NOTATION.UPPER_RIGHT.map((toothNumber, index) => (
                        <ToothSVG
                          key={toothNumber}
                          toothNumber={toothNumber}
                          status={dentalData[toothNumber] || "healthy"}
                          onClick={
                            chartInfo
                              ? (status) =>
                                  handleToothClick(toothNumber, status)
                              : undefined
                          }
                          size={getToothConfig.size}
                          isSelected={index === 0}
                          position="upper right"
                          disabled={!chartInfo}
                        />
                      ))}
                    </div>
                  </div>
                </Grid>

                {/* Upper Left */}
                <Grid item xs={12}>
                  <div
                    className={classes.quadrant}
                    {...getQuadrantAriaProps(
                      t("Upper Left"),
                      FDI_NOTATION.UPPER_LEFT.length
                    )}
                    style={getQuadrantConfig("upper-left")}
                  >
                    {shouldRender.quadrantTitles && (
                      <Typography className={classes.quadrantTitle}>
                        {t("Upper Left")} (2°)
                      </Typography>
                    )}
                    <div className={classes.toothContainer}>
                      {FDI_NOTATION.UPPER_LEFT.map((toothNumber) => (
                        <ToothSVG
                          key={toothNumber}
                          toothNumber={toothNumber}
                          status={dentalData[toothNumber] || "healthy"}
                          onClick={
                            chartInfo
                              ? (status) =>
                                  handleToothClick(toothNumber, status)
                              : undefined
                          }
                          size={getToothConfig.size}
                          position="upper left"
                          disabled={!chartInfo}
                        />
                      ))}
                    </div>
                  </div>
                </Grid>

                {/* Lower Right */}
                <Grid item xs={12}>
                  <div
                    className={classes.quadrant}
                    {...getQuadrantAriaProps(
                      t("Lower Right"),
                      FDI_NOTATION.LOWER_RIGHT.length
                    )}
                    style={getQuadrantConfig("lower-right")}
                  >
                    {shouldRender.quadrantTitles && (
                      <Typography className={classes.quadrantTitle}>
                        {t("Lower Right")} (4°)
                      </Typography>
                    )}
                    <div className={classes.toothContainer}>
                      {FDI_NOTATION.LOWER_RIGHT.map((toothNumber) => (
                        <ToothSVG
                          key={toothNumber}
                          toothNumber={toothNumber}
                          status={dentalData[toothNumber] || "healthy"}
                          onClick={
                            chartInfo
                              ? (status) =>
                                  handleToothClick(toothNumber, status)
                              : undefined
                          }
                          size={getToothConfig.size}
                          position="lower right"
                          disabled={!chartInfo}
                        />
                      ))}
                    </div>
                  </div>
                </Grid>

                {/* Lower Left */}
                <Grid item xs={12}>
                  <div
                    className={classes.quadrant}
                    {...getQuadrantAriaProps(
                      t("Lower Left"),
                      FDI_NOTATION.LOWER_LEFT.length
                    )}
                    style={getQuadrantConfig("lower-left")}
                  >
                    {shouldRender.quadrantTitles && (
                      <Typography className={classes.quadrantTitle}>
                        {t("Lower Left")} (3°)
                      </Typography>
                    )}
                    <div className={classes.toothContainer}>
                      {FDI_NOTATION.LOWER_LEFT.map((toothNumber) => (
                        <ToothSVG
                          key={toothNumber}
                          toothNumber={toothNumber}
                          status={dentalData[toothNumber] || "healthy"}
                          onClick={
                            chartInfo
                              ? (status) =>
                                  handleToothClick(toothNumber, status)
                              : undefined
                          }
                          size={getToothConfig.size}
                          position="lower left"
                          disabled={!chartInfo}
                        />
                      ))}
                    </div>
                  </div>
                </Grid>
              </Grid>
            ) : (
              // Layout grid para tablet/desktop
              <>
                {/* Cuadrantes Superiores */}
                <Grid container spacing={getGridProps.spacing}>
                  <Grid
                    item
                    xs={getGridProps.xs}
                    sm={getGridProps.sm}
                    md={getGridProps.md}
                    lg={getGridProps.lg}
                  >
                    <div
                      className={classes.quadrant}
                      {...getQuadrantAriaProps(
                        t("Upper Right"),
                        FDI_NOTATION.UPPER_RIGHT.length
                      )}
                      style={getQuadrantConfig("upper-right")}
                    >
                      <Typography className={classes.quadrantTitle}>
                        {t("Upper Right")} (1°)
                      </Typography>
                      <div className={classes.toothContainer}>
                        {FDI_NOTATION.UPPER_RIGHT.map((toothNumber, index) => (
                          <ToothSVG
                            key={toothNumber}
                            toothNumber={toothNumber}
                            status={dentalData[toothNumber] || "healthy"}
                            onClick={
                              chartInfo
                                ? (status) =>
                                    handleToothClick(toothNumber, status)
                                : undefined
                            }
                            size={getToothConfig.size}
                            isSelected={index === 0}
                            position="upper right"
                            disabled={!chartInfo}
                          />
                        ))}
                      </div>
                    </div>
                  </Grid>
                  <Grid
                    item
                    xs={getGridProps.xs}
                    sm={getGridProps.sm}
                    md={getGridProps.md}
                    lg={getGridProps.lg}
                  >
                    <div
                      className={classes.quadrant}
                      {...getQuadrantAriaProps(
                        t("Upper Left"),
                        FDI_NOTATION.UPPER_LEFT.length
                      )}
                      style={getQuadrantConfig("upper-left")}
                    >
                      <Typography className={classes.quadrantTitle}>
                        {t("Upper Left")} (2°)
                      </Typography>
                      <div className={classes.toothContainer}>
                        {FDI_NOTATION.UPPER_LEFT.map((toothNumber) => (
                          <ToothSVG
                            key={toothNumber}
                            toothNumber={toothNumber}
                            status={dentalData[toothNumber] || "healthy"}
                            onClick={
                              chartInfo
                                ? (status) =>
                                    handleToothClick(toothNumber, status)
                                : undefined
                            }
                            size={getToothConfig.size}
                            position="upper left"
                            disabled={!chartInfo}
                          />
                        ))}
                      </div>
                    </div>
                  </Grid>
                </Grid>

                {/* Cuadrantes Inferiores */}
                <Grid container spacing={getGridProps.spacing}>
                  <Grid
                    item
                    xs={getGridProps.xs}
                    sm={getGridProps.sm}
                    md={getGridProps.md}
                    lg={getGridProps.lg}
                  >
                    <div
                      className={classes.quadrant}
                      {...getQuadrantAriaProps(
                        t("Lower Right"),
                        FDI_NOTATION.LOWER_RIGHT.length
                      )}
                      style={getQuadrantConfig("lower-right")}
                    >
                      <Typography className={classes.quadrantTitle}>
                        {t("Lower Right")} (4°)
                      </Typography>
                      <div className={classes.toothContainer}>
                        {FDI_NOTATION.LOWER_RIGHT.map((toothNumber) => (
                          <ToothSVG
                            key={toothNumber}
                            toothNumber={toothNumber}
                            status={dentalData[toothNumber] || "healthy"}
                            onClick={
                              chartInfo
                                ? (status) =>
                                    handleToothClick(toothNumber, status)
                                : undefined
                            }
                            size={getToothConfig.size}
                            position="lower right"
                            disabled={!chartInfo}
                          />
                        ))}
                      </div>
                    </div>
                  </Grid>
                  <Grid
                    item
                    xs={getGridProps.xs}
                    sm={getGridProps.sm}
                    md={getGridProps.md}
                    lg={getGridProps.lg}
                  >
                    <div
                      className={classes.quadrant}
                      {...getQuadrantAriaProps(
                        t("Lower Left"),
                        FDI_NOTATION.LOWER_LEFT.length
                      )}
                      style={getQuadrantConfig("lower-left")}
                    >
                      <Typography className={classes.quadrantTitle}>
                        {t("Lower Left")} (3°)
                      </Typography>
                      <div className={classes.toothContainer}>
                        {FDI_NOTATION.LOWER_LEFT.map((toothNumber) => (
                          <ToothSVG
                            key={toothNumber}
                            toothNumber={toothNumber}
                            status={dentalData[toothNumber] || "healthy"}
                            onClick={
                              chartInfo
                                ? (status) =>
                                    handleToothClick(toothNumber, status)
                                : undefined
                            }
                            size={getToothConfig.size}
                            position="lower left"
                            disabled={!chartInfo}
                          />
                        ))}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </>
            )}
          </div>
        </div>

        {/* Leyenda */}
        <DentalChartLegend />

        {chartInfo?.notes && (
          <Box mt={2}>
            <Typography variant="subtitle2">{t("Notes")}:</Typography>
            <Typography variant="body2">{chartInfo.notes}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DentalChartComponent;
